import React, { useState } from 'react';

/**
 * KPICardOperations - KPI Card following Finance pattern
 * Shows: Label, Value, vs Budget badge, YoY badge, Trend icon
 */

// Semantic color system
const statusColors = {
  positive: '#0C8A4B',
  positiveBg: '#EAF7EF',
  warning: '#A07A18',
  warningBg: '#F8F1DD',
  negative: '#C33D52',
  negativeBg: '#FBECEF',
};

const getSemanticBadgeStyle = (val, inverse = false, useWarning = true) => {
  if (val === undefined || val === null) return { bg: statusColors.positiveBg, text: statusColors.positive };
  const isFavorable = inverse ? val <= 0 : val >= 0;
  const absVal = Math.abs(val);
  if (useWarning && !isFavorable && absVal <= 5) {
    return { bg: statusColors.warningBg, text: statusColors.warning };
  }
  if (isFavorable) {
    return { bg: statusColors.positiveBg, text: statusColors.positive };
  }
  return { bg: statusColors.negativeBg, text: statusColors.negative };
};

const getTrendIconInfo = (vsBudget, vsPrior, inverse = false) => {
  const mainVal = vsBudget !== undefined ? vsBudget : vsPrior;
  if (mainVal === undefined) return null;
  const isFavorable = inverse ? mainVal <= 0 : mainVal >= 0;
  const isUp = mainVal > 0;
  return {
    isUp,
    bgColor: isFavorable ? statusColors.positiveBg : statusColors.negativeBg,
    iconColor: isFavorable ? statusColors.positive : statusColors.negative,
  };
};

// Tooltip Component with Drill-Down
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading, drillDown, language }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-72 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">
            {language === 'pt' ? 'Definição' : 'Definition'}
          </p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">
            {language === 'pt' ? 'Relevância' : 'Relevance'}
          </p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">
            {language === 'pt' ? 'Leitura Actual' : 'Current Reading'}
          </p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
        {/* Drill-Down Section */}
        {drillDown && drillDown.length > 0 && (
          <div className="pt-2 border-t border-slate-700/40">
            <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
              {language === 'pt' ? 'Decomposição' : 'Breakdown'}
            </p>
            <div className="space-y-1">
              {drillDown.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-400">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{item.value}</span>
                    {item.variance !== undefined && (
                      <span className={`${item.variance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {item.variance >= 0 ? '+' : ''}{item.variance}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const KPICardOperations = ({ 
  label, 
  value, 
  unit, 
  vsBudget, 
  vsPrior, 
  changeUnit,
  inverse = false, 
  highlight = false, 
  tooltipData,
  useWarning = true,
  budgetLabel = 'vs Orç.',
  yoyLabel = 'YoY',
  language = 'pt'
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Determine change unit: if KPI is in % and no explicit changeUnit, use p.p.
  const effectiveChangeUnit = changeUnit || (unit === '%' ? 'p.p.' : '%');
  const trendInfo = getTrendIconInfo(vsBudget, vsPrior, inverse);
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const priorStyle = getSemanticBadgeStyle(vsPrior, inverse, useWarning);
  
  return (
    <div 
      className={`rounded-xl border p-5 transition-all duration-300 hover:shadow-lg hover:border-gold-300/50 overflow-visible relative ${highlight ? 'bg-slate-50 border-slate-300' : 'bg-white border-slate-200'}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
          drillDown={tooltipData.drillDown}
          language={language}
        />
      )}
      
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Label, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Label */}
          <p className="text-[11px] text-slate-500 tracking-wider uppercase mb-1.5">{label}</p>
          
          {/* Value + Unit */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-[28px] leading-[32px] font-semibold tracking-[-0.5px] text-primary">
              {value}
            </span>
            <span className="text-[13px] text-slate-400">{unit}</span>
          </div>
          
          {/* Comparison Badges - Same Line */}
          <div className="flex items-center gap-2 flex-wrap">
            {vsBudget !== undefined && (
              <span 
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                style={{ backgroundColor: budgetStyle.bg, color: budgetStyle.text }}
              >
                {vsBudget >= 0 ? '↗' : '↘'} {vsBudget >= 0 ? '+' : ''}{vsBudget.toFixed(1)}{effectiveChangeUnit} {budgetLabel}
              </span>
            )}
            {vsPrior !== undefined && (
              <span 
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                style={{ backgroundColor: priorStyle.bg, color: priorStyle.text }}
              >
                {vsPrior >= 0 ? '↗' : '↘'} {vsPrior >= 0 ? '+' : ''}{vsPrior.toFixed(1)}{effectiveChangeUnit} {yoyLabel}
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Trend Arrow Icon */}
        {trendInfo && (
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: trendInfo.iconColor }}>
                <path d="M8 3L14 9L12.6 10.4L9 6.8V13H7V6.8L3.4 10.4L2 9L8 3Z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: trendInfo.iconColor }}>
                <path d="M8 13L2 7L3.4 5.6L7 9.2V3H9V9.2L12.6 5.6L14 7L8 13Z" fill="currentColor"/>
              </svg>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KPICardOperations;
